import React, { useCallback, useEffect, useState, useRef } from "react";
import MenuComponent from "../MenuComponent";
import IconComponent from "@CubeComponents/icons";
import Text from "../Text/Text";
import { useTranslation } from "@i18n";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";

type RowSizeKey = "small" | "medium" | "large" | "wrap";

type SlickGridLike = {
  setOptions: (opts: { rowHeight: number }) => void;
  resizeCanvas: () => void;
  render: () => void;
  getOptions?: () => { rowHeight?: number };
  getOptionsInternal?: () => { rowHeight?: number };
};

export type RowResizeModalProps = {
  gridRef?: React.RefObject<unknown>;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  open?: boolean;
  handleRowResizeOptionClick?: (size: RowSizeKey, px: number) => void;
  setEnableWrapText?: (enable: boolean) => void;
  currentRowSize: RowSizeKey;
  groupConditions?: unknown[];
  enableWrapText?: boolean;
  setCurrentRowSize: React.Dispatch<React.SetStateAction<RowSizeKey>>;
};

const ROW_SIZES: Record<RowSizeKey, number> = {
  small: 48,
  medium: 72,
  large: 96,
  // Per-LINE unit (keep in sync with DEFAULT_ROW_SIZES.wrap): wrapped rows span N of these,
  // so height tracks the text instead of adding a full 48px row per wrapped line.
  wrap: 22,
};

const pxToSizeKey = (h: number): RowSizeKey => {
  if (h >= 88) return "large";
  if (h >= 60) return "medium";
  return "small";
};

const DisplaySettingsPopper: React.FC<RowResizeModalProps> = ({
  gridRef,
  anchorEl,
  onClose,
  open,
  handleRowResizeOptionClick,
  setEnableWrapText,
  currentRowSize,
  setCurrentRowSize,
  groupConditions,
  enableWrapText,
}) => {
  const [settingsAnchor, setSettingsAnchor] = useState<HTMLElement | null>(null);

  const safeRef = useRef<SlickGridLike | null>(null);
  const finalGridRef = gridRef || safeRef;

  const getGrid = useCallback((): SlickGridLike | null => {
    const refVal = finalGridRef?.current ?? null;
    if (!refVal) return null;
    // The ref may hold either the grid itself or a wrapper exposing it as `slickGrid`.
    if ((refVal as { slickGrid?: SlickGridLike }).slickGrid) return (refVal as { slickGrid: SlickGridLike }).slickGrid;
    return refVal as SlickGridLike;
  }, [finalGridRef]);

  useEffect(() => {
    const grid = getGrid();
    const opts = grid?.getOptions?.() ?? grid?.getOptionsInternal?.() ?? ({} as { rowHeight?: number });
    const rh = typeof opts?.rowHeight === "number" ? opts.rowHeight : ROW_SIZES.small;
    setCurrentRowSize(pxToSizeKey(rh));
  }, [getGrid, anchorEl, setCurrentRowSize]);

  const applyRowHeight = useCallback(
    (size: RowSizeKey) => {
      const grid = getGrid();
      const px = ROW_SIZES[size];
      if (grid) {
        grid.setOptions({ rowHeight: px });
        grid.resizeCanvas();
        grid.render();
      }
      setCurrentRowSize(size);
      handleRowResizeOptionClick?.(size, px);
      setSettingsAnchor(null);
    },
    [getGrid, handleRowResizeOptionClick, setCurrentRowSize],
  );

  const isOpen = typeof open === "boolean" ? open : Boolean(anchorEl);
  const { t } = useTranslation();

  return (
    <>
      {/* --- FIRST POPPER: Display As --- */}
      <MenuComponent
        open={isOpen && !settingsAnchor}
        anchorEl={anchorEl}
        onClose={onClose}
        placement="bottom"
        width={350}
        disableCloseOnMouseLeave
        listSx={{ padding: "1rem", borderRadius: "0.5rem" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className="flex flex-col gap-4 min-w-[12rem] p-2 " aria-label="Display As ">
          <div className="border-b border-[#C6D8EC]">
            <div className="text-[#192048] font-semibold text-xl mb-3 leading-tight  ">
              <Text>{t("Display As")}</Text>
            </div>
          </div>

          <div className="flex items-center gap-4 w-full rounded-lg hover:bg-[#F5FAFF] mt-3">
            <div className="flex items-center gap-2">
              <IconComponent name="ListViewIcon" width="1rem" height="1rem" />
              <Text className="text-[#192048] text-sm">{t("List")}</Text>
            </div>

            <div className="flex items-center gap-2 w-full justify-between">
              <span className=" bg-[#6BBBE3] text-white text-xs font-semibold px-2 py-1 rounded-md w-fit">
                {t("Current")}
              </span>
              <ButtonComponent
                buttonType={BUTTON_TYPES.TEXT_BUTTON}
                type="button"
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => setSettingsAnchor(e.currentTarget)}
                aria-label="Open properties"
              >
                <IconComponent name="Conditions" width="1rem" height="1rem" />
              </ButtonComponent>
            </div>
          </div>
        </div>
      </MenuComponent>

      {/* --- SECOND POPPER: Properties / Row Height --- */}
      <MenuComponent
        open={Boolean(settingsAnchor)}
        anchorEl={anchorEl}
        onClose={() => setSettingsAnchor(null)}
        placement="bottom"
        width={350}
        disableCloseOnMouseLeave
        listSx={{ padding: "1rem", borderRadius: "0.5rem" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
      >
        <div className="flex flex-col gap-4 min-w-48 " aria-label="Properties">
          <div className="flex items-center gap-2 text-[#192048] font-semibold text-xl border-b border-[#C6D8EC]  pb-4 ">
            <ButtonComponent
              buttonType={BUTTON_TYPES.TEXT_BUTTON}
              onClick={() => setSettingsAnchor(null)}
              aria-label="Back"
            >
              <IconComponent name="ArrowLeft" width="1rem" height="1rem" />
            </ButtonComponent>
            <Text>{t("Properties")}</Text>
          </div>

          <div className="text-[#192048] font-bold text-sm">
            <Text>{t("Row Height")}</Text>
          </div>

          <div className="flex flex-col gap-2">
            {(["small", "medium", "large", "wrap"] as RowSizeKey[]).map((k: RowSizeKey) => {
              const isCurrent =
                k === "wrap" ? enableWrapText || currentRowSize === "wrap" : !enableWrapText && currentRowSize === k;

              const label = k.charAt(0).toUpperCase() + k.slice(1);

              let iconName: string;
              if (k === "small") {
                iconName = "RowResizeIcon";
              } else if (k === "wrap") {
                iconName = "WrapText";
              } else if (k === "medium") {
                iconName = "MediumCell";
              } else {
                iconName = "LargeRowSize";
              }

              return (
                <button
                  key={k}
                  onClick={() => {
                    if ((groupConditions?.length ?? 0) > 0 && k === "wrap") return;
                    if (k === "wrap") {
                      // Apply wrap
                      setEnableWrapText?.(true);
                      setCurrentRowSize("wrap");
                      handleRowResizeOptionClick?.(k, ROW_SIZES[k]);
                      setSettingsAnchor(null);
                    } else {
                      // Apply row size and disable wrap
                      applyRowHeight(k);
                      setEnableWrapText?.(false);
                    }
                  }}
                  className={`
  flex
  ${isCurrent ? "bg-[#6BBBE31F]" : "hover:bg-[#F5FAFF]"}
  items-center
  ${(groupConditions?.length ?? 0) > 0 && k === "wrap" ? "opacity-40 pointer-events-none" : ""}
  justify-between
  w-full rounded-lg px-3 py-2
`}
                >
                  <div className="flex items-center gap-2 text-[#192048] text-sm">
                    <IconComponent name={iconName} width="1rem" height="1rem" />
                    <Text className="inline">{label}</Text>
                  </div>

                  <div className="flex items-center gap-2">
                    {isCurrent && (
                      <Text className="inline-block bg-[#6BBBE3] text-white text-xs font-semibold px-2 py-1 rounded-md">
                        {t("Current")}
                      </Text>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </MenuComponent>
    </>
  );
};

DisplaySettingsPopper.displayName = "DisplaySettingsPopper";

export default DisplaySettingsPopper;
